import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, RefreshCw, Send, FileText, Ban, FilePlus2, Download, FileCheck2, AlertTriangle, XCircle } from 'lucide-react';
import { StatCard, PageHeader } from '@/cajita/ui';
import { Money } from '@/shared/display/Money';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  listDocuments,
  resendDocument,
  voidDocument,
  createNote,
  pdfUrl,
  type DocumentSummary,
  type DocumentType,
} from './documents.api';
import { VoidDialog } from './VoidDialog';
import { CreditNoteDialog } from './CreditNoteDialog';

const TYPE_LABEL: Record<DocumentType, string> = {
  FACTURA: 'Factura',
  BOLETA: 'Boleta',
  NOTA_CREDITO: 'N/C',
  NOTA_DEBITO: 'N/D',
};

const SUNAT_BADGE: Record<string, { cls: string; label: string }> = {
  PENDING: { cls: 'badge-gray', label: 'Pendiente' },
  SENT: { cls: 'badge-blue', label: 'Enviado' },
  ACCEPTED: { cls: 'badge-green', label: 'Aceptado' },
  OBSERVED: { cls: 'badge-amber', label: 'Observado' },
  REJECTED: { cls: 'badge-red', label: 'Rechazado' },
};

const FILTERS = ['Todos', 'ACCEPTED', 'PENDING', 'OBSERVED', 'REJECTED'] as const;
const FILTER_LABEL: Record<string, string> = { Todos: 'Todos', ACCEPTED: 'Aceptados', PENDING: 'Pendientes', OBSERVED: 'Observados', REJECTED: 'Rechazados' };

export function DocumentsPage(): JSX.Element {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchRaw, setSearchRaw] = useState('');
  const [filt, setFilt] = useState<string>('Todos');
  const search = useDebouncedValue(searchRaw, 300);
  const [voidTarget, setVoidTarget] = useState<DocumentSummary | null>(null);
  const [noteTarget, setNoteTarget] = useState<DocumentSummary | null>(null);

  const query = useQuery({
    queryKey: ['documents', { page, search }],
    queryFn: () => listDocuments({ page, pageSize: 20, search: search || undefined }),
    placeholderData: (prev) => prev,
    refetchInterval: 8_000,
  });

  const resend = useMutation({
    mutationFn: (id: string) => resendDocument(id),
    onSuccess: () => {
      toast.success('Reenviado a la cola');
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const voidMut = useMutation({
    mutationFn: (args: { id: string; reason: string }) => voidDocument(args.id, args.reason),
    onSuccess: (data) => {
      toast.success(`Anulado. Resumen: ${data.summaryId ?? 'N/A'}`);
      setVoidTarget(null);
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const noteMut = useMutation({
    mutationFn: (args: { affectedDocumentId: string; type: 'NOTA_CREDITO' | 'NOTA_DEBITO'; reasonCode: string; notes?: string }) =>
      createNote(args),
    onSuccess: (data) => {
      toast.success(`Nota ${data.fullNumber} emitida`);
      setNoteTarget(null);
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));
  const rows = filt === 'Todos' ? items : items.filter((d) => d.sunatStatus === filt);

  const counts = useMemo(() => {
    const c = { ACCEPTED: 0, OBSERVED: 0, REJECTED: 0 };
    for (const d of items) if (d.sunatStatus in c) c[d.sunatStatus as keyof typeof c]++;
    return c;
  }, [items]);

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Comprobantes"
          sub="Facturas, boletas y notas electrónicas · se actualiza cada 8s"
          actions={
            <>
              <button className="btn btn-secondary" onClick={() => query.refetch()} disabled={query.isFetching}>
                <RefreshCw size={17} className={query.isFetching ? 'animate-spin' : ''} />
                Actualizar
              </button>
              <button className="btn btn-secondary">
                <Download size={17} />
                Exportar
              </button>
            </>
          }
        />

        <div className="kpi-grid" style={{ marginBottom: 20 }}>
          <StatCard label="Emitidos (total)" value={String(total)} icon={<FileText size={19} />} accent="#3B82F6" />
          <StatCard label="Aceptados SUNAT" value={String(counts.ACCEPTED)} icon={<FileCheck2 size={19} />} accent="#22C55E" sub="en esta página" />
          <StatCard label="Observados" value={String(counts.OBSERVED)} icon={<AlertTriangle size={19} />} accent="#F59E0B" />
          <StatCard label="Rechazados" value={String(counts.REJECTED)} icon={<XCircle size={19} />} accent="#EF4444" />
        </div>

        <div className="card">
          <div className="tbl-toolbar">
            <div className="input-icon" style={{ width: 280 }}>
              <Search size={17} />
              <input
                className="input"
                placeholder="Buscar por número, cliente, RUC/DNI…"
                value={searchRaw}
                onChange={(e) => {
                  setSearchRaw(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="row gap6 wrap">
              {FILTERS.map((s) => (
                <button key={s} className={`chip ${filt === s ? 'on' : ''}`} onClick={() => setFilt(s)}>
                  {FILTER_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {query.isLoading ? (
            <SkeletonRows cols={6} />
          ) : rows.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">
                <FileText size={28} />
              </div>
              <div>
                <div className="t-h2" style={{ marginBottom: 4 }}>
                  {search || filt !== 'Todos' ? 'Sin resultados' : 'Sin comprobantes'}
                </div>
                <div className="muted">{search || filt !== 'Todos' ? 'Prueba otra búsqueda o filtro.' : 'Emite tu primera boleta o factura desde el POS.'}</div>
              </div>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Serie-Número</th>
                    <th>Cliente</th>
                    <th>Tipo</th>
                    <th>Estado SUNAT</th>
                    <th className="right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => {
                    const b = SUNAT_BADGE[d.sunatStatus] ?? { cls: 'badge-gray', label: d.sunatStatus };
                    const canNote = (d.sunatStatus === 'ACCEPTED' || d.sunatStatus === 'OBSERVED') && (d.type === 'FACTURA' || d.type === 'BOLETA');
                    const canResend = d.sunatStatus === 'PENDING' || d.sunatStatus === 'REJECTED';
                    return (
                      <tr key={d.id}>
                        <td className="tnum">
                          <DateDisplay value={d.issueDate} />
                        </td>
                        <td>
                          <span className="strong tnum" style={{ fontFamily: 'var(--font)' }}>
                            {d.fullNumber}
                          </span>
                        </td>
                        <td>
                          <div className="strong">{d.customerName}</div>
                          <div className="t-xs muted tnum">
                            {d.customerDocType} {d.customerDocNumber}
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-gray">{TYPE_LABEL[d.type]}</span>
                        </td>
                        <td>
                          <span className={`badge ${b.cls}`}>
                            <span className="bdot" />
                            {b.label}
                          </span>
                        </td>
                        <td className="right tnum strong">
                          <Money value={d.grandTotal} currency={d.currency} />
                        </td>
                        <td className="right">
                          <div className="row gap4" style={{ justifyContent: 'flex-end' }}>
                            <a className="btn btn-ghost btn-sm btn-icon" href={pdfUrl(d.id)} target="_blank" rel="noopener" title="Ver PDF">
                              <FileText size={16} />
                            </a>
                            {canNote && (
                              <>
                                <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setNoteTarget(d)} title="Emitir nota">
                                  <FilePlus2 size={16} />
                                </button>
                                <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} onClick={() => setVoidTarget(d)} title="Anular">
                                  <Ban size={16} />
                                </button>
                              </>
                            )}
                            {canResend && (
                              <button className="btn btn-ghost btn-sm" onClick={() => resend.mutate(d.id)} disabled={resend.isPending} title="Reenviar">
                                <Send size={15} />
                                Reenviar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="tbl-foot">
            <span>
              Mostrando {rows.length} de {total}
            </span>
            <div className="row gap6">
              <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <span className="t-sm muted" style={{ padding: '0 6px' }}>
                {page} / {totalPages}
              </span>
              <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>

      <VoidDialog
        open={!!voidTarget}
        onOpenChange={(o) => !o && setVoidTarget(null)}
        fullNumber={voidTarget?.fullNumber ?? ''}
        isSubmitting={voidMut.isPending}
        onConfirm={(reason) => voidTarget && voidMut.mutate({ id: voidTarget.id, reason })}
      />
      <CreditNoteDialog
        open={!!noteTarget}
        onOpenChange={(o) => !o && setNoteTarget(null)}
        fullNumber={noteTarget?.fullNumber ?? ''}
        isSubmitting={noteMut.isPending}
        onConfirm={(input) => noteTarget && noteMut.mutate({ affectedDocumentId: noteTarget.id, ...input })}
      />
    </div>
  );
}

function SkeletonRows({ cols, rows = 7 }: { cols: number; rows?: number }): JSX.Element {
  return (
    <div style={{ padding: '8px 0' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="sk" style={{ height: 12, flex: c === 0 ? 2 : 1, opacity: 1 - r * 0.08 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Error';
}
